import React from "react"
import { useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { useLanguage } from "@/contexts/LanguageContext"

// Import real API hooks
import {
  useBuildings,
  useBuilding,
  useCreateBuilding,
  useUpdateBuilding,
  useDeleteBuilding,
  useImportBuildingsCSV,
  useDownloadBuildingsTemplate,
} from "@/hooks/useBuildings"
import {
  getBuildingPaymentConfig,
  updateBuildingPaymentConfig,
  type UpdateBuildingPaymentConfigData,
} from "@/api/buildings"
import type { BuildingList, Building, CreateBuildingData } from "@/api/buildings"
import { getHOAAdminProfile } from "@/api/settings"
import BulkImportModal from "@/components/BulkImportModal"

// Icons
import { Search, Upload, Plus, Building2, MapPin, Users, Home, Edit, Trash2, Eye, CheckCircle2, XCircle, MoreHorizontal } from "lucide-react"

/** =========================================================
 * Types & Constants
 * ========================================================= */
type SortOption = "name-asc" | "name-desc" | "code-asc" | "code-desc" | "city-asc" | "city-desc" | "units-asc" | "units-desc"

/* ================= Design Tokens ================= */
const PRIMARY = "#1F3A93"
/** Quiet outline style: white bg + black text, visible on glass */
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50"
const MAX_RIF_UPLOAD_MB = 10
const MAX_RIF_UPLOAD_BYTES = MAX_RIF_UPLOAD_MB * 1024 * 1024

type BuildingPaymentFormData = {
  bank_name: string
  bank_account_number: string
  bank_routing_number: string
  account_holder_name: string
  zelle_email: string
  zelle_phone: string
  paypal_email: string
  pago_movil_bank: string
  pago_movil_phone: string
  pago_movil_id_number: string
  cash_instructions: string
}

type BuildingPaymentConfigSubmit = {
  data: UpdateBuildingPaymentConfigData
  shouldSave: boolean
}

function getEmptyBuildingPaymentFormData(): BuildingPaymentFormData {
  return {
    bank_name: "",
    bank_account_number: "",
    bank_routing_number: "",
    account_holder_name: "",
    zelle_email: "",
    zelle_phone: "",
    paypal_email: "",
    pago_movil_bank: "",
    pago_movil_phone: "",
    pago_movil_id_number: "",
    cash_instructions: "",
  }
}

function hasAnyPaymentFormValue(data: BuildingPaymentFormData): boolean {
  return Object.values(data).some((value) => String(value || "").trim().length > 0)
}

type TranslateFn = (key: string, vars?: Record<string, any>) => string

function getSubscriptionStatusLabel(status: string | null | undefined, t: TranslateFn): string {
  if (!status) return t("common.unknown")
  if (status === "active") return t("superAdmin.hoaAdmins.status.active")
  if (status === "suspended") return t("superAdmin.hoaAdmins.status.suspended")
  if (status === "expired") return t("superAdmin.hoaAdmins.status.expired")
  if (status === "cancelled") return t("superAdmin.hoaAdmins.status.cancelled")
  return t("common.unknown")
}

function getBuildingSubmitErrorMessage(err: any, t: TranslateFn): string {
  const isTimeout = err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message ?? ""))
  const hasNoResponse = !err?.response
  const status = err?.response?.status
  const data = err?.response?.data

  if (isTimeout) return t("hoa.buildings.errors.timeout")
  if (hasNoResponse) return t("hoa.buildings.errors.network")

  if (status === 413) {
    return t("hoa.buildings.errors.rifTooLarge", { maxMb: MAX_RIF_UPLOAD_MB })
  }

  if (status === 401) return t("hoa.buildings.errors.unauthorized")
  if (status === 403) return t("hoa.buildings.errors.forbidden")
  if (status === 404) return t("hoa.buildings.errors.notFound")
  if (status === 409) return t("hoa.buildings.errors.conflict")
  if (status === 415) return t("hoa.buildings.errors.rifInvalidType")
  if (status === 429) return t("hoa.buildings.errors.rateLimited")
  if (status && status >= 500) return t("hoa.buildings.errors.server")

  const detail = typeof data?.detail === "string" ? data.detail : ""
  if (detail.includes("NO_PACKAGE")) return t("hoa.buildings.errors.noPackage")
  if (detail.includes("LIMIT_REACHED")) return t("hoa.buildings.errors.limitReached")

  const rifErrors = data?.rif_image
  if (rifErrors) {
    const text = Array.isArray(rifErrors) ? rifErrors.join(" ") : String(rifErrors)
    if (/too large|file too large|size|exceed|payload|413/i.test(text)) {
      return t("hoa.buildings.errors.rifTooLarge", { maxMb: MAX_RIF_UPLOAD_MB })
    }
    if (/invalid|image|file|format|type/i.test(text)) {
      return t("hoa.buildings.errors.rifInvalidType")
    }
    return t("hoa.buildings.errors.rifUploadFailed")
  }

  const hasKnownFieldError = ["code", "name", "non_field_errors"]
    .map((k) => data?.[k])
    .some((v) => Array.isArray(v) && v.length > 0)

  if (hasKnownFieldError) return t("hoa.buildings.errors.validation")
  if (status === 400 || status === 422) return t("hoa.buildings.errors.validation")
  if (typeof detail === "string" && detail.trim()) return t("hoa.buildings.errors.validation")

  const hasGenericApiField = data && typeof data === "object"
    ? Object.values(data).find((v) => Array.isArray(v) && v.length > 0)
    : null
  if (hasGenericApiField && Array.isArray(hasGenericApiField)) {
    return t("hoa.buildings.errors.validation")
  }

  if (typeof err?.message === "string" && err.message.includes("413")) {
    return t("hoa.buildings.errors.rifTooLarge", { maxMb: MAX_RIF_UPLOAD_MB })
  }

  return t("hoa.buildings.errors.generic")
}

/** =========================================================
 * Main Component
 * ========================================================= */
export default function BuildingsPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { t } = useLanguage()

  const [q, setQ] = React.useState("")
  const [sortBy, setSortBy] = React.useState<SortOption>("name-asc")
  const [filterActive, setFilterActive] = React.useState<"all" | "active" | "inactive">("all")
  const [sheet, setSheet] =
    React.useState<null | { mode: "create" } | { mode: "edit"; building: BuildingList; buildingId: string }>(null)
  const [confirmDel, setConfirmDel] = React.useState<BuildingList | null>(null)
  const [showImportModal, setShowImportModal] = React.useState(false)

  // Real API hooks
  const listQ = useBuildings()
  const profileQ = useQuery({
    queryKey: ["hoa-admin-profile"],
    queryFn: getHOAAdminProfile,
    staleTime: 60_000,
  })
  const createMut = useCreateBuilding()
  const updateMut = useUpdateBuilding()
  const deleteMut = useDeleteBuilding()
  const importMut = useImportBuildingsCSV()
  const downloadTemplateMut = useDownloadBuildingsTemplate()

  const data = listQ.data ?? []

  // Filter by search query and active status
  const filtered = React.useMemo(() => {
    return data.filter((b) => {
      // Active filter
      if (filterActive === "active" && !b.is_active) return false
      if (filterActive === "inactive" && b.is_active) return false

      // Search filter
      if (q) {
        const hay = [
          b.code,
          b.name,
          b.city ?? "",
          b.state ?? "",
          b.address ?? "",
        ].join(" ").toLowerCase()
        return hay.includes(q.toLowerCase())
      }
      return true
    })
  }, [data, q, filterActive])

  // Sort the filtered results
  const sorted = React.useMemo(() => {
    const copy = [...filtered]
    switch (sortBy) {
      case "name-asc":
        return copy.sort((a, b) => a.name.localeCompare(b.name))
      case "name-desc":
        return copy.sort((a, b) => b.name.localeCompare(a.name))
      case "code-asc":
        return copy.sort((a, b) => a.code.localeCompare(b.code))
      case "code-desc":
        return copy.sort((a, b) => b.code.localeCompare(a.code))
      case "city-asc":
        return copy.sort((a, b) => (a.city ?? "").localeCompare(b.city ?? ""))
      case "city-desc":
        return copy.sort((a, b) => (b.city ?? "").localeCompare(a.city ?? ""))
      case "units-asc":
        return copy.sort((a, b) => a.total_units - b.total_units)
      case "units-desc":
        return copy.sort((a, b) => b.total_units - a.total_units)
      default:
        return copy
    }
  }, [filtered, sortBy])

  // KPIs
  const total = data.length
  const active = data.filter((b) => b.is_active).length
  const inactive = total - active
  const totalUnits = data.reduce((sum, b) => sum + b.unit_count, 0)
  const totalResidents = data.reduce((sum, b) => sum + b.resident_count, 0)

  // Calculate average occupancy
  const avgOccupancy = React.useMemo(() => {
    const buildingsWithUnits = data.filter(b => b.unit_count > 0)
    if (buildingsWithUnits.length === 0) return 0
    const totalOccupancy = buildingsWithUnits.reduce((sum, b) => {
      const occupied = b.resident_count > 0 ? b.unit_count : 0
      return sum + (occupied / b.unit_count * 100)
    }, 0)
    return totalOccupancy / buildingsWithUnits.length
  }, [data])

  const createBlockedBySubscription =
    !!profileQ.data &&
    profileQ.data.subscription_status !== "active"

  const subscriptionStatusLabel = getSubscriptionStatusLabel(profileQ.data?.subscription_status, t)
  const createBlockedMessage = t("hoa.buildings.errors.subscriptionBlocked", {
    status: subscriptionStatusLabel,
  })

  return (
    <div className="space-y-5 sm:space-y-6 relative">
      {/* Soft ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-[18px] font-semibold text-gray-900 flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          {t("hoa.buildings.title")}
        </h1>
        <p className="text-sm text-gray-600">
          {t("hoa.buildings.subtitle")}
        </p>
      </div>

      {/* Search + Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 min-w-0">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("hoa.buildings.searchPlaceholder")}
            className="h-11 rounded-2xl pl-10 bg-white/90 backdrop-blur ring-1 ring-black/10 placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-black/10"
          />
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowImportModal(true)}
            className={`${QUIET} h-11 px-4 flex-1 sm:flex-none`}
          >
            <Upload className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("hoa.buildings.importCsv")}</span>
          </Button>
          <Button
            onClick={() => setSheet({ mode: "create" })}
            style={{ backgroundColor: PRIMARY }}
            className="h-11 px-5 rounded-2xl text-white hover:opacity-90 flex-1 sm:flex-none"
          >
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("admin.addBuilding")}</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 whitespace-nowrap">{t("common.status")}:</span>
          <Select value={filterActive} onValueChange={(v) => setFilterActive(v as any)}>
            <SelectTrigger className={`w-full sm:w-[140px] h-9 rounded-xl ${QUIET}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              <SelectItem value="active">{t("hoa.buildings.status.activeOnly")}</SelectItem>
              <SelectItem value="inactive">{t("hoa.buildings.status.inactiveOnly")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 whitespace-nowrap">{t("common.sortBy")}:</span>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
            <SelectTrigger className={`w-full sm:w-[180px] h-9 rounded-xl ${QUIET}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name-asc">{t("hoa.buildings.sort.nameAsc")}</SelectItem>
              <SelectItem value="name-desc">{t("hoa.buildings.sort.nameDesc")}</SelectItem>
              <SelectItem value="code-asc">{t("hoa.buildings.sort.codeAsc")}</SelectItem>
              <SelectItem value="code-desc">{t("hoa.buildings.sort.codeDesc")}</SelectItem>
              <SelectItem value="city-asc">{t("hoa.buildings.sort.cityAsc")}</SelectItem>
              <SelectItem value="city-desc">{t("hoa.buildings.sort.cityDesc")}</SelectItem>
              <SelectItem value="units-asc">{t("hoa.buildings.sort.unitsAsc")}</SelectItem>
              <SelectItem value="units-desc">{t("hoa.buildings.sort.unitsDesc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI cards (glass) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label={t("hoa.buildings.kpi.totalBuildings")} value={total} icon={Building2} />
        <KpiCard label={t("hoa.buildings.kpi.active")} value={active} tone="green" icon={CheckCircle2} />
        <KpiCard label={t("hoa.buildings.kpi.inactive")} value={inactive} tone="gray" icon={XCircle} />
        <KpiCard label={t("hoa.buildings.kpi.totalUnits")} value={totalUnits} tone="blue" icon={Home} />
        <KpiCard label={t("hoa.buildings.kpi.totalResidents")} value={totalResidents} tone="purple" icon={Users} />
      </div>

      {/* List */}
      <div className="space-y-3">
        {listQ.isLoading ? (
          <>
            <BuildingSkeleton />
            <BuildingSkeleton />
            <BuildingSkeleton />
            <BuildingSkeleton />
          </>
        ) : sorted.length === 0 ? (
          <Card variant="glass" className="rounded-3xl">
            <CardContent className="p-8 text-center text-sm text-gray-600">
              {data.length === 0 ? (
                <div className="space-y-3">
                  <Building2 className="h-12 w-12 mx-auto text-gray-400" />
                  <p>{t("hoa.buildings.emptyNone")}</p>
                </div>
              ) : (
                <p>{t("hoa.buildings.emptyFiltered")}</p>
              )}
            </CardContent>
          </Card>
        ) : (
          sorted.map((b) => (
            <BuildingCard
              key={b.id}
              building={b}
              onView={() => navigate(`/hoa-admin/buildings/${b.id}`)}
              onEdit={() => setSheet({ mode: "edit", building: b, buildingId: b.id })}
              onDelete={() => setConfirmDel(b)}
            />
          ))
        )}
      </div>

      {/* Create/Edit Sheet */}
      {sheet && (
        <BuildingFormSheet
          mode={sheet.mode}
          buildingId={sheet.mode === "edit" ? sheet.buildingId : undefined}
          open={!!sheet}
          onOpenChange={(open) => !open && setSheet(null)}
          onSubmit={(data, rifImageFile, paymentConfig) => {
            const onError = (err: any) => {
              toast.error(getBuildingSubmitErrorMessage(err, t), { duration: 7000 })
            }

            if (sheet.mode === "create") {
              void (async () => {
                try {
                  const createdBuilding = await createMut.mutateAsync({ data, rifImageFile })

                  if (paymentConfig.shouldSave) {
                    await updateBuildingPaymentConfig(createdBuilding.id, paymentConfig.data)
                  }

                  setSheet(null)
                } catch (err: any) {
                  onError(err)
                }
              })()
            } else if (sheet.mode === "edit") {
              void (async () => {
                try {
                  const updatedBuilding = await updateMut.mutateAsync({
                    id: sheet.buildingId,
                    data,
                    rifImageFile,
                  })

                  if (paymentConfig.shouldSave) {
                    await updateBuildingPaymentConfig(updatedBuilding.id, paymentConfig.data)
                  }

                  setSheet(null)
                } catch (err: any) {
                  onError(err)
                }
              })()
            }
          }}
          isSubmitting={createMut.isPending || updateMut.isPending}
          createBlocked={sheet.mode === "create" && createBlockedBySubscription}
          createBlockedMessage={createBlockedMessage}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!confirmDel} onOpenChange={(open) => !open && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("hoa.buildings.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("hoa.buildings.deleteDesc", { name: confirmDel?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDel) {
                  deleteMut.mutate(confirmDel.id, {
                    onSuccess: () => setConfirmDel(null),
                  })
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CSV Import Modal */}
      <BulkImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        type="buildings"
        onImport={async (file) => {
          const result = await importMut.mutateAsync(file)
          qc.invalidateQueries({ queryKey: ["buildings"] })
          setShowImportModal(false)
          // Convert CSVImportResult to ImportResult format
          return {
            success: result.success,
            total_processed: result.total_processed,
            success_count: result.success_count,
            error_count: result.error_count,
            errors: result.errors.map(err => ({
              row: err.row,
              field: "multiple",
              message: err.errors.join(", "),
              data: {}
            })),
            created_unit_ids: result.created_building_ids ?? []
          }
        }}
        onDownloadTemplate={async () => {
          await downloadTemplateMut.mutateAsync()
        }}
        importing={importMut.isPending}
      />
    </div>
  )
}

/** =========================================================
 * Building Card Component
 * ========================================================= */
interface BuildingCardProps {
  building: BuildingList
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}

function BuildingCard({ building, onView, onEdit, onDelete }: BuildingCardProps) {
  const occupancyRate = building.unit_count > 0 
    ? Math.round((building.resident_count / building.unit_count) * 100) 
    : 0
  const { t } = useLanguage()
  const [dropdownOpen, setDropdownOpen] = React.useState(false)

  const paymentStatus = building.payment_config_status ?? "MISSING"
  const paymentBadgeClass =
    paymentStatus === "CONFIGURED"
      ? "bg-emerald-100 text-emerald-700"
      : paymentStatus === "HOA_DEFAULT"
        ? "bg-amber-100 text-amber-700"
        : "bg-rose-100 text-rose-700"
  const paymentBadgeLabel =
    paymentStatus === "CONFIGURED"
      ? "Payment: Building"
      : paymentStatus === "HOA_DEFAULT"
        ? "Payment: HOA Default"
        : "Payment: Missing"

  const handleCardClick = () => {
    // Don't navigate if dropdown is open
    if (!dropdownOpen) {
      onView()
    }
  }

  return (
    <Card 
      variant="glass" 
      className="rounded-3xl overflow-hidden hover:shadow-md transition-all cursor-pointer group"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          handleCardClick()
        }
      }}
      aria-label={t("hoa.buildings.viewAria", { name: building.name })}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col gap-4">
          {/* Header Row: Icon + Name + Badges + Actions */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="grid size-10 place-items-center rounded-2xl bg-white ring-1 ring-black/10 shadow-sm text-[#1F3A93] flex-shrink-0 group-hover:shadow-md transition-shadow">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="text-[15px] font-semibold text-gray-900 truncate group-hover:text-[#1F3A93] transition-colors">
                    {building.name}
                  </h3>
                  <Badge
                    variant="secondary"
                    className="text-[11px] font-medium px-2 py-0.5"
                  >
                    {building.code}
                  </Badge>
                  {building.is_active ? (
                    <Badge className="text-[11px] bg-green-100 text-green-700 hover:bg-green-100">
                      {t("hoa.buildings.active")}
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[11px]">
                      {t("hoa.buildings.inactive")}
                    </Badge>
                  )}
                  <Badge className={`text-[11px] hover:opacity-90 ${paymentBadgeClass}`}>
                    {paymentBadgeLabel}
                  </Badge>
                </div>
                
                {/* Location */}
                {(building.city || building.state) && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">
                      {[building.city, building.state].filter(Boolean).join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions Dropdown */}
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-9 rounded-xl bg-white hover:bg-gray-50 border border-gray-200 shadow-sm flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                  aria-label={t("hoa.buildings.actions")}
                >
                  <MoreHorizontal className="h-4 w-4 text-gray-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-xl">
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation()
                    setDropdownOpen(false)
                    onView()
                  }}
                  className="cursor-pointer"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {t("common.viewDetails")}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation()
                    setDropdownOpen(false)
                    onEdit()
                  }}
                  className="cursor-pointer"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t("hoa.buildings.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation()
                    setDropdownOpen(false)
                    onDelete()
                  }}
                  className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("hoa.buildings.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-gray-100">
            <StatItem
              icon={Home}
              label={t("admin.units")}
              value={building.unit_count.toString()}
              subValue={`/${building.total_units}`}
            />
            <StatItem
              icon={Users}
              label={t("admin.residents")}
              value={building.resident_count.toString()}
            />
            <StatItem
              icon={Building2}
              label={t("hoa.buildings.floors")}
              value={building.floors.toString()}
            />
            <div className="flex flex-col">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                  {t("hoa.buildings.occupancy")}
                </span>
              </div>
              <div className={`text-sm font-semibold ${occupancyRate > 80 ? "text-green-600" : occupancyRate > 50 ? "text-amber-600" : "text-red-600"}`}>
                {occupancyRate}%
              </div>
              <div className="h-1.5 mt-1 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full transition-all rounded-full"
                  style={{ 
                    width: `${occupancyRate}%`,
                    backgroundColor: occupancyRate > 80 ? '#22c55e' : occupancyRate > 50 ? '#f59e0b' : '#ef4444'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/** =========================================================
 * Stat Item Component
 * ========================================================= */
interface StatItemProps {
  icon?: any
  label: string
  value: string
  subValue?: string
  tone?: "green" | "amber" | "red" | "blue" | "purple"
}

function StatItem({ icon: Icon, label, value, subValue, tone }: StatItemProps) {
  const toneClasses = {
    green: "text-green-600",
    amber: "text-amber-600",
    red: "text-red-600",
    blue: "text-blue-600",
    purple: "text-purple-600",
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 mb-0.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-gray-500" />}
        <span className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</span>
      </div>
      <div className={`text-sm font-semibold ${tone ? toneClasses[tone] : "text-gray-900"}`}>
        {value}
        {subValue && <span className="text-gray-400 text-xs ml-0.5">{subValue}</span>}
      </div>
    </div>
  )
}

/** =========================================================
 * KPI Card Component
 * ========================================================= */
interface KpiCardProps {
  label: string
  value: number
  tone?: "green" | "amber" | "gray" | "blue" | "purple"
  icon?: any
}

function KpiCard({ label, value, tone, icon: Icon }: KpiCardProps) {
  const { language } = useLanguage()
  const locale = language === "es" ? "es-ES" : "en-US"

  const bg = {
    green: "from-green-50/80 to-green-100/50",
    amber: "from-amber-50/80 to-amber-100/50",
    gray: "from-gray-50/80 to-gray-100/50",
    blue: "from-blue-50/80 to-blue-100/50",
    purple: "from-purple-50/80 to-purple-100/50",
  }[tone || "green"]

  const textColor = {
    green: "text-green-700",
    amber: "text-amber-700",
    gray: "text-gray-700",
    blue: "text-blue-700",
    purple: "text-purple-700",
  }[tone || "green"]

  return (
    <Card variant="glass" className="rounded-2xl overflow-hidden">
      <CardContent className={`p-4 bg-gradient-to-br ${bg}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-600 font-medium uppercase tracking-wide">{label}</span>
          {Icon && <Icon className={`h-4 w-4 ${textColor}`} />}
        </div>
        <div className={`text-2xl font-bold ${textColor}`}>{value.toLocaleString(locale)}</div>
      </CardContent>
    </Card>
  )
}

/** =========================================================
 * Building Form Sheet
 * ========================================================= */
interface BuildingFormSheetProps {
  mode: "create" | "edit"
  buildingId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (
    data: CreateBuildingData,
    rifImageFile: File | null,
    paymentConfig: BuildingPaymentConfigSubmit
  ) => void
  isSubmitting: boolean
  createBlocked?: boolean
  createBlockedMessage?: string
}

function BuildingFormSheet({
  mode,
  buildingId,
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  createBlocked = false,
  createBlockedMessage,
}: BuildingFormSheetProps) {
  const { t } = useLanguage()
  // Fetch full building data for edit mode
  const buildingQ = useBuilding(buildingId!)
  const buildingPaymentConfigQ = useQuery({
    queryKey: ["building-payment-config", buildingId],
    queryFn: () => getBuildingPaymentConfig(buildingId!),
    enabled: mode === "edit" && !!buildingId,
  })
  const building = mode === "edit" ? buildingQ.data : undefined
  const paymentStatus = buildingPaymentConfigQ.data?.payment_config_status ?? "MISSING"
  const paymentStatusClass =
    paymentStatus === "CONFIGURED"
      ? "bg-emerald-100 text-emerald-700"
      : paymentStatus === "HOA_DEFAULT"
        ? "bg-amber-100 text-amber-700"
        : "bg-rose-100 text-rose-700"

  const [formData, setFormData] = React.useState<CreateBuildingData>({
    code: "",
    name: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "USA",
    total_units: 0,
    floors: 1,
    default_monthly_fee: 0,
    is_active: true,
    notes: "",
    rif: "",
  })
  const [rifImageFile, setRifImageFile] = React.useState<File | null>(null)
  const [rifImageError, setRifImageError] = React.useState<string | null>(null)
  const [paymentFormData, setPaymentFormData] = React.useState<BuildingPaymentFormData>(
    getEmptyBuildingPaymentFormData()
  )
  const rifImageInputRef = React.useRef<HTMLInputElement>(null)

  // Update form data when building data is loaded
  React.useEffect(() => {
    if (building) {
      setFormData({
        code: building.code,
        name: building.name,
        address: building.address ?? "",
        city: building.city ?? "",
        state: building.state ?? "",
        postal_code: building.postal_code ?? "",
        country: building.country ?? "USA",
        total_units: building.total_units,
        floors: building.floors,
        default_monthly_fee: building.default_monthly_fee ? parseFloat(building.default_monthly_fee) : 0,
        is_active: building.is_active,
        notes: building.notes ?? "",
        rif: building.rif ?? "",
        })
        setRifImageFile(null)
        setRifImageError(null)
      }
  }, [building])

  React.useEffect(() => {
    if (mode !== "edit") {
      setPaymentFormData(getEmptyBuildingPaymentFormData())
      return
    }

    const config = buildingPaymentConfigQ.data?.config
    if (!config) {
      setPaymentFormData(getEmptyBuildingPaymentFormData())
      return
    }

    setPaymentFormData({
      bank_name: config.bank_name ?? "",
      bank_account_number: config.bank_account_number ?? "",
      bank_routing_number: config.bank_routing_number ?? "",
      account_holder_name: config.account_holder_name ?? "",
      zelle_email: config.zelle_email ?? "",
      zelle_phone: config.zelle_phone ?? "",
      paypal_email: config.paypal_email ?? "",
      pago_movil_bank: config.pago_movil_bank ?? "",
      pago_movil_phone: config.pago_movil_phone ?? "",
      pago_movil_id_number: config.pago_movil_id_number ?? "",
      cash_instructions: config.cash_instructions ?? "",
    })
  }, [mode, buildingPaymentConfigQ.data?.config])

  const handleChange = (field: keyof CreateBuildingData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handlePaymentChange = (field: keyof BuildingPaymentFormData, value: string) => {
    setPaymentFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === "create" && createBlocked) {
      toast.error(createBlockedMessage ?? t("hoa.buildings.errors.subscriptionBlockedTitle"))
      return
    }
    if (rifImageError) {
      toast.error(rifImageError)
      return
    }
    onSubmit(formData, rifImageFile, {
      data: paymentFormData,
      shouldSave:
        hasAnyPaymentFormValue(paymentFormData) ||
        Boolean(buildingPaymentConfigQ.data?.config),
    })
  }

  const handleRifImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null

    if (!selected) {
      setRifImageFile(null)
      setRifImageError(null)
      return
    }

    const validType = selected.type.startsWith("image/")
    if (!validType) {
      setRifImageFile(null)
      setRifImageError(t("hoa.buildings.errors.rifInvalidType"))
      if (rifImageInputRef.current) rifImageInputRef.current.value = ""
      return
    }

    if (selected.size > MAX_RIF_UPLOAD_BYTES) {
      setRifImageFile(null)
      setRifImageError(t("hoa.buildings.errors.rifTooLarge", { maxMb: MAX_RIF_UPLOAD_MB }))
      if (rifImageInputRef.current) rifImageInputRef.current.value = ""
      return
    }

    setRifImageFile(selected)
    setRifImageError(null)
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onOpenChange(false) }}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden rounded-3xl bg-white/95 backdrop-blur-xl ring-1 ring-black/10">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {mode === "create" ? t("hoa.buildings.form.addTitle") : t("hoa.buildings.form.editTitle")}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? t("hoa.buildings.form.addDesc")
              : t("hoa.buildings.form.editDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="overflow-y-auto px-1 -mx-1 space-y-6 py-4" style={{maxHeight: "calc(85vh - 180px)"}}>
          {mode === "create" && createBlocked && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
              <p className="text-sm font-semibold">{t("hoa.buildings.errors.subscriptionBlockedTitle")}</p>
              <p className="text-xs mt-1">{createBlockedMessage}</p>
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildings.form.basicInfo")}</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">
                  {t("hoa.buildings.form.code")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleChange("code", e.target.value)}
                  placeholder={t("hoa.buildings.form.codePlaceholder")}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">
                  {t("common.name")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder={t("hoa.buildings.form.namePlaceholder")}
                  required
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">{t("common.address")}</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder={t("hoa.buildings.form.addressPlaceholder")}
                rows={2}
                className="rounded-xl resize-none"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">{t("hoa.buildings.form.city")}</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  placeholder={t("hoa.buildings.form.cityPlaceholder")}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="state">{t("hoa.buildings.form.state")}</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  placeholder={t("hoa.buildings.form.statePlaceholder")}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="postal_code">{t("hoa.buildings.form.postalCode")}</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => handleChange("postal_code", e.target.value)}
                  placeholder={t("hoa.buildings.form.postalCodePlaceholder")}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">{t("hoa.buildings.form.country")}</Label>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => handleChange("country", e.target.value)}
                placeholder={t("hoa.buildings.form.countryPlaceholder")}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rif">{t("buildings.rif")}</Label>
              <Input
                id="rif"
                value={formData.rif}
                onChange={(e) => handleChange("rif", e.target.value)}
                placeholder={t("buildings.rifPlaceholder")}
                className="rounded-xl"
              />
            </div>

            {/* RIF Image */}
            <div className="space-y-2">
              <Label>{t("hoa.buildings.form.rifImageLabel")} <span className="text-gray-400 font-normal">({t("common.optional")})</span></Label>
              <div className="flex items-center gap-3">
                <input
                  ref={rifImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleRifImageChange}
                />
                <button
                  type="button"
                  onClick={() => rifImageInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  {rifImageFile ? rifImageFile.name : t("hoa.buildings.form.chooseFile")}
                </button>
                {rifImageFile && (
                  <button type="button" onClick={() => { setRifImageFile(null); if (rifImageInputRef.current) rifImageInputRef.current.value = ""; }} className="text-xs text-rose-500 hover:text-rose-700">{t("hoa.buildings.form.removeFile")}</button>
                )}
              </div>
              {rifImageError && (
                <p className="text-xs text-rose-600">{rifImageError}</p>
              )}
              {/* Show existing image link when editing */}
              {mode === "edit" && building?.rif_image && !rifImageFile && (
                <a href={building.rif_image} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline mt-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                  {t("hoa.buildings.form.viewCurrentRifImage")}
                </a>
              )}
            </div>
          </div>

          {/* Specifications */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildings.form.specs")}</h3>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total_units">{t("hoa.buildings.form.totalUnits")}</Label>
                <Input
                  id="total_units"
                  type="number"
                  min="0"
                  value={formData.total_units}
                  onChange={(e) => handleChange("total_units", parseInt(e.target.value) || 0)}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="floors">{t("hoa.buildings.floors")}</Label>
                <Input
                  id="floors"
                  type="number"
                  min="1"
                  value={formData.floors}
                  onChange={(e) => handleChange("floors", parseInt(e.target.value) || 1)}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_monthly_fee">{t("hoa.buildings.form.defaultFee")}</Label>
                <Input
                  id="default_monthly_fee"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.default_monthly_fee}
                  onChange={(e) => handleChange("default_monthly_fee", parseFloat(e.target.value) || 0)}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Payment Routing */}
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Payment Routing Details</h3>
              {mode === "edit" && (
                <Badge className={`text-[11px] ${paymentStatusClass}`}>
                  {paymentStatus === "CONFIGURED"
                    ? "Building configured"
                    : paymentStatus === "HOA_DEFAULT"
                      ? "Using HOA default"
                      : "Missing destination"}
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Optional. These details define where this building's HOA payments are routed.
            </p>

            {mode === "edit" && buildingPaymentConfigQ.isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Skeleton className="h-10 rounded-xl" />
                <Skeleton className="h-10 rounded-xl" />
                <Skeleton className="h-10 rounded-xl" />
                <Skeleton className="h-10 rounded-xl" />
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("settings.bankTransferDetails")}</h4>
                  <div className="space-y-2">
                    <Label htmlFor="payment_bank_name">{t("settings.bankName")}</Label>
                    <Input
                      id="payment_bank_name"
                      value={paymentFormData.bank_name}
                      onChange={(e) => handlePaymentChange("bank_name", e.target.value)}
                      placeholder={t("settings.exBanesco")}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment_bank_account_number">{t("settings.accountNumber")}</Label>
                      <Input
                        id="payment_bank_account_number"
                        value={paymentFormData.bank_account_number}
                        onChange={(e) => handlePaymentChange("bank_account_number", e.target.value)}
                        placeholder={t("settings.accountNumberPlaceholder")}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment_bank_routing_number">{t("settings.routingNumber")}</Label>
                      <Input
                        id="payment_bank_routing_number"
                        value={paymentFormData.bank_routing_number}
                        onChange={(e) => handlePaymentChange("bank_routing_number", e.target.value)}
                        placeholder={t("settings.routingPlaceholder")}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payment_account_holder_name">{t("settings.accountHolderName")}</Label>
                    <Input
                      id="payment_account_holder_name"
                      value={paymentFormData.account_holder_name}
                      onChange={(e) => handlePaymentChange("account_holder_name", e.target.value)}
                      placeholder={t("settings.accountHolderPlaceholder")}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("settings.paymentMethod.zelle")}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment_zelle_email">{t("settings.zelleEmail")}</Label>
                      <Input
                        id="payment_zelle_email"
                        type="email"
                        value={paymentFormData.zelle_email}
                        onChange={(e) => handlePaymentChange("zelle_email", e.target.value)}
                        placeholder={t("settings.zelleEmailPlaceholder")}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment_zelle_phone">{t("settings.zellePhone")}</Label>
                      <Input
                        id="payment_zelle_phone"
                        value={paymentFormData.zelle_phone}
                        onChange={(e) => handlePaymentChange("zelle_phone", e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("settings.paymentMethod.pagoMovil")}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment_pago_movil_bank">{t("settings.bank")}</Label>
                      <Input
                        id="payment_pago_movil_bank"
                        value={paymentFormData.pago_movil_bank}
                        onChange={(e) => handlePaymentChange("pago_movil_bank", e.target.value)}
                        placeholder={t("settings.exMercantil")}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment_pago_movil_id_number">{t("settings.idNumber")}</Label>
                      <Input
                        id="payment_pago_movil_id_number"
                        value={paymentFormData.pago_movil_id_number}
                        onChange={(e) => handlePaymentChange("pago_movil_id_number", e.target.value)}
                        placeholder={t("settings.exIdNumber")}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="payment_pago_movil_phone">{t("settings.phone")}</Label>
                      <Input
                        id="payment_pago_movil_phone"
                        value={paymentFormData.pago_movil_phone}
                        onChange={(e) => handlePaymentChange("pago_movil_phone", e.target.value)}
                        placeholder="+58 (414) 123-4567"
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("settings.otherPaymentMethods")}</h4>
                  <div className="space-y-2">
                    <Label htmlFor="payment_paypal_email">{t("settings.paypalEmail")}</Label>
                    <Input
                      id="payment_paypal_email"
                      type="email"
                      value={paymentFormData.paypal_email}
                      onChange={(e) => handlePaymentChange("paypal_email", e.target.value)}
                      placeholder={t("settings.paypalEmailPlaceholder")}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment_cash_instructions">{t("settings.cashInstructionsTitle")}</Label>
                  <Textarea
                    id="payment_cash_instructions"
                    value={paymentFormData.cash_instructions}
                    onChange={(e) => handlePaymentChange("cash_instructions", e.target.value)}
                    placeholder={t("settings.cashInstructionsPlaceholder")}
                    rows={3}
                    className="rounded-xl resize-none"
                  />
                </div>

                {mode === "edit" &&
                  buildingPaymentConfigQ.data?.fallback_payment_details &&
                  paymentStatus !== "CONFIGURED" && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                      <p className="text-xs">
                        This building currently falls back to HOA-level payment details until its own destination is fully configured.
                      </p>
                    </div>
                  )}
              </>
            )}
          </div>

          {/* Additional Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildings.form.additionalInfo")}</h3>

            <div className="space-y-2">
              <Label htmlFor="notes">{t("payments.notes")}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder={t("hoa.buildings.form.notesPlaceholder")}
                rows={3}
                className="rounded-xl resize-none"
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
              <div className="space-y-0.5">
                <Label htmlFor="is_active" className="text-sm font-medium">
                  {t("hoa.buildings.form.activeStatus")}
                </Label>
                <p className="text-xs text-gray-500">
                  {t("hoa.buildings.form.activeHint")}
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => handleChange("is_active", checked)}
              />
            </div>
          </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className={QUIET}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || (mode === "create" && createBlocked)}
              style={{ backgroundColor: PRIMARY }}
              className="text-white hover:opacity-90 rounded-xl shadow-lg hover:shadow-xl"
            >
              {isSubmitting
                ? t("common.saving")
                : mode === "create"
                ? t("hoa.buildings.form.create")
                : t("common.saveChanges")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** =========================================================
 * Skeleton Component
 * ========================================================= */
function BuildingSkeleton() {
  return (
    <Card variant="glass" className="rounded-3xl">
      <CardContent className="p-5">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <Skeleton className="size-10 rounded-2xl" />
              <div className="space-y-2 flex-1">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-48 rounded" />
                  <Skeleton className="h-5 w-16 rounded" />
                </div>
                <Skeleton className="h-4 w-32 rounded" />
              </div>
            </div>
            <Skeleton className="size-9 rounded-xl" />
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-gray-100">
            <Skeleton className="h-12 w-full rounded" />
            <Skeleton className="h-12 w-full rounded" />
            <Skeleton className="h-12 w-full rounded" />
            <Skeleton className="h-12 w-full rounded" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
